import { type ResidentItem, ResidentStatus } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { useCallback } from 'react'
import { type UseQueryOptions, useQuery, useQueryClient } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'

type UseResidentsListProps = {
  filters?: Record<string, any>
  options?: Pick<UseQueryOptions<{ result: ResidentItem[] }>, 'onSuccess'>
}

type UseResidentsListReturn = {
  residents: ResidentItem[]
  activeResidents: ResidentItem[]
  isLoading: boolean
  refetch: () => void
}

export const RESIDENTS_URL = '/api/list/residents'

const useResidentsList = ({
  filters = {},
  options = {}
}: UseResidentsListProps = {}): UseResidentsListReturn => {
  const queryClient = useQueryClient()

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const onSuccess = useCallback(
    (data: { result: ResidentItem[] }) => {
      options?.onSuccess?.(data)

      const residents = data.result

      residents.forEach((resident) => {
        queryClient.setQueryData(['resident', resident.id], {
          result: resident
        })
      })
    },
    [options?.onSuccess]
  )

  const params = getQueryParams(filters)
  const { data, isLoading, refetch } = useQuery<{ result: ResidentItem[] }>(
    ['residents', params],
    () => getJSON({ url: `${RESIDENTS_URL}?${params}` }),
    { ...options, onSuccess }
  )

  const residents = data?.result

  return {
    residents: residents || [],
    activeResidents:
      residents?.filter(
        (resident) => resident.status === ResidentStatus.active
      ) || [],
    isLoading,
    refetch
  }
}

export default useResidentsList
